import { LocalDate } from "@js-joda/core";
import { SalaryType } from "model/Salary";
import type { CreateWorkerEmploymentRequest } from "services/api/company/staff.service";
import {
  getEmploymentFieldVisibility,
  getVacationFieldVisibility,
} from "web-apps/company/components/staff/components/shared/staffEmploymentFieldVisibility.logic";
import type {
  SaveEmploymentParams,
  SaveEmploymentResult,
} from "web-apps/company/components/staff/components/profilePanel/profilePanel.contract";
import {
  parseIsoDateToLocalDateOrNull,
  toIsoStartOfDayUtcFromStockholmDate,
} from "web-apps/company/utils/timeAndDate";

export function toLocalDateOrNull(
  value: string | null | undefined,
): LocalDate | null {
  return parseIsoDateToLocalDateOrNull(value);
}

export function toIsoDateStartOfDayUtc(date: LocalDate): string {
  return toIsoStartOfDayUtcFromStockholmDate(date);
}

function toIsoDateStartOfDayUtcOrNull(value: string | null): string | null {
  const parsedDate = toLocalDateOrNull(value);
  if (parsedDate == null) return null;
  return toIsoDateStartOfDayUtc(parsedDate);
}

export function toNonNegativeNumber(value: string): number {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return 0;
  return Math.max(0, parsedValue);
}

export function toNonNegativeIntegerOrZero(value: string): number {
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) return 0;
  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) return 0;
  return parsedValue;
}

function toEmploymentBasePayload(
  params: SaveEmploymentParams,
): Omit<CreateWorkerEmploymentRequest, "salaryValue"> {
  const form = params.editedEmploymentDetails;

  const workPercentagePercent = toNonNegativeNumber(form.workPercentage);
  const workPercentage =
    form.salaryType === SalaryType.Monthly
      ? Math.min(workPercentagePercent / 100, 1)
      : 0;

  const employmentType = form.employmentType || "Undefined";

  return {
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    endDate: params.effectiveEndDate
      ? toIsoDateStartOfDayUtc(params.effectiveEndDate)
      : null,
    employmentType,
    employeeNumber: toNonNegativeIntegerOrZero(form.employeeNumber),
    employeeCategory: form.employeeCategory || "BlueCollar",
    salaryType: form.salaryType || "Undefined",
    workPercentage,
    paymentType: form.paymentType || "None",
    role: form.role.trim(),
    agreement: form.agreement.trim(),
    vacationDays: toNonNegativeIntegerOrZero(form.vacationDays),
    noticeDays: toNonNegativeIntegerOrZero(form.noticeDays),
    workplace: form.workplace.trim(),
    probationPeriodEndDate: toIsoDateStartOfDayUtcOrNull(
      params.probationPeriodEndDate,
    ),
  };
}

export function buildUpdateStaffWorkerPayload(
  params: SaveEmploymentParams,
): SaveEmploymentResult {
  return {
    payload: toEmploymentBasePayload(params),
  };
}

export function buildCreateWorkerEmploymentPayload(
  params: SaveEmploymentParams,
): { payload: CreateWorkerEmploymentRequest } {
  const form = params.editedEmploymentDetails;
  const base = toEmploymentBasePayload(params);
  const visibility = getEmploymentFieldVisibility(form.employmentType);
  const salaryType = visibility.showSalaryType
    ? form.salaryType || "Undefined"
    : "Hourly";
  const vacationVisibility = getVacationFieldVisibility(form.salaryType);
  const toVacationEarningDays = (value: string): number =>
    vacationVisibility.showVacationEarningDays
      ? toNonNegativeIntegerOrZero(value)
      : 0;

  return {
    payload: {
      ...base,
      vacationPayoutType: vacationVisibility.showVacationPayoutType
        ? form.vacationPayoutType
        : "Undefined",
      maxSickLeaveDays: toVacationEarningDays(form.maxSickLeaveDays),
      maxCareOfChildDays: toVacationEarningDays(form.maxCareOfChildDays),
      maxParentalLeaveDays: toVacationEarningDays(form.maxParentalLeaveDays),
      advanceVacationDays: toVacationEarningDays(form.advanceVacationDays),
      salaryType,
      employeeCategory: visibility.showEmployeeCategory
        ? base.employeeCategory
        : "BlueCollar",
      paymentType: visibility.showPaymentType ? base.paymentType : "None",
      workPercentage: visibility.showSalaryType ? base.workPercentage : 0,
      agreement: visibility.showContractTerms ? base.agreement : "",
      vacationDays: visibility.showContractTerms ? base.vacationDays : 0,
      noticeDays: visibility.showContractTerms ? base.noticeDays : 0,
      workplace: visibility.showContractTerms ? base.workplace : "",
      salaryValue: visibility.showSalary ? toNonNegativeNumber(form.salary) : 0,
    },
  };
}
